import React from 'react';
import { useTranslation } from 'react-i18next';
// import { FcGoogle } from 'react-icons/fc';
import {
  Form,
  useActionData,
  useLoaderData,
  useNavigate,
  useNavigation,
  useSearchParams,
} from 'react-router';

import { Assets } from '~/assets.g/assets';
import { AppRoutes } from '~/core/router/routes';
import { isValidEmail } from '~/core/utils/email_validator';
import { CustomButton } from '~/shared/components/custom_button';
import { CustomInput } from '~/shared/components/custom_input_field';

import { LoginHandler } from '../../application/login_handler';
import { AuthLayout } from '../layouts/auth_layout';

export const loader = LoginHandler.loginLoader;
export const action = LoginHandler.loginAction;

export default function LoginPage() {
  const { t } = useTranslation(['auth']);
  const navigate = useNavigate();
  const navigation = useNavigation();
  const [searchParams] = useSearchParams();

  const currentStep = Number.parseInt(searchParams.get('step') || '1', 10);
  // const [currentStep, setCurrentStep] = React.useState<number>(1);

  const [formErrors, setFormErrors] = React.useState<{
    email?: string;
  }>({});
  const [resetEmail] = React.useState('');
  const [isLocalSubmitting, setIsLocalSubmitting] = React.useState(false);

  const { authFormData, version } = useLoaderData<typeof LoginHandler.loginLoader>();
  const actionData = useActionData<typeof LoginHandler.loginAction>();

  // Get organization and email from URL params
  const selectedOrg = searchParams.get('selectedOrganization');
  const userEmail = searchParams.get('userEmail');

  const isSubmitting = navigation.state === 'submitting';

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const email = formData.get('email') as string;

    const errors: typeof formErrors = {};

    if (!isValidEmail(email)) {
      errors.email = t('invalidEmail');
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setIsLocalSubmitting(true);
    event.currentTarget.submit();
  };

  const renderStep1 = () => (
    <Form
      method="post"
      className="flex flex-col"
    >
      <CustomInput
        name="organizationName"
        label={t('organizationName')}
        labelType="required"
        placeholder={t('enterOrganizationName')}
        defaultValue={
          selectedOrg
            ? JSON.parse(selectedOrg).name
            : authFormData?.inviteInfo?.organization?.name || ''
        }
      />

      <div className="mb-6 md:mb-8" />

      <CustomInput
        name="email"
        type="email"
        label={t('workEmail')}
        labelType="required"
        placeholder={t('enterWorkMail')}
        // readOnly={!!authFormData?.inviteInfo}
        defaultValue={userEmail || authFormData?.inviteInfo?.user?.email || ''}
      />

      <div className="mb-6 md:mb-8" />

      <CustomButton
        variant="primary"
        buttonType="filled"
        isFullWidth
        isLoading={isSubmitting || isLocalSubmitting}
        type="submit"
      >
        {t('continue')}
      </CustomButton>
    </Form>
  );

  const renderStep2 = () => (
    <Form
      method="post"
      className="flex flex-col"
      onSubmit={handleSubmit}
    >
      <div className="mb-4">
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:gap-2">
          <p className="text-sm md:text-base">
            <span className="text-neutralDark">{t('loggingInAs')}</span>
          </p>
          <p className="break-words text-sm font-semibold md:flex-1 md:text-base">
            {authFormData?.email || authFormData?.inviteInfo?.user?.email}
          </p>

          {authFormData?.organizationName && (
            <CustomButton
              variant="primary"
              buttonType="text"
              onClick={() => navigate('/login')}
              type="button"
              className="self-start md:ml-auto md:flex-shrink-0"
            >
              {t('change')}
            </CustomButton>
          )}
        </div>
      </div>
      <CustomInput
        name="organizationName"
        label={t('organizationName')}
        placeholder={t('enterOrganizationName')}
        value={authFormData?.organizationName || ''}
        readOnly
      />

      <div className="mb-6 md:mb-8" />
      <CustomInput
        name="email"
        type="email"
        label={t('yourEmail')}
        placeholder={t('enterWorkMail')}
        value={authFormData?.email || ''}
        readOnly
      />

      <div className="mb-6 md:mb-8" />

      <CustomInput
        name="password"
        label={t('enterPassword')}
        labelType="required"
        placeholder={t('password')}
        type="password"
      />

      <div className="mb-4 md:mb-5" />

      <div>
        <CustomButton
          variant="primary"
          buttonType="text"
          onClick={() => navigate(`${AppRoutes.LOGIN.path}?step=3`)}
          type="button"
        >
          {t('forgotPassword')}
        </CustomButton>
      </div>

      <div className="mb-5 md:mb-6" />

      <CustomButton
        variant="primary"
        buttonType="filled"
        isFullWidth
        isLoading={isSubmitting || isLocalSubmitting}
        type="submit"
      >
        {t('continue')}
      </CustomButton>
    </Form>
  );

  // Password reset has no backend capability yet (no Firebase/API call exists
  // to send a reset email) — this is an honest "not available" state rather
  // than faking a delay and a success screen that never actually sends anything.
  const renderStep3 = () => (
    <div className="flex flex-col">
      <CustomButton
        variant="primary"
        buttonType="filled"
        isFullWidth
        onClick={() => navigate(`${AppRoutes.LOGIN.path}?step=1`)}
      >
        {t('backToLogin')}
      </CustomButton>
    </div>
  );

  const renderStep4 = () => (
    <Form
      method="post"
      className="flex flex-col"
    >
      <div className="mb-4 md:mb-5" />
      <div className="flex flex-col gap-3 md:flex-row md:gap-5">
        <CustomButton
          variant="surface"
          buttonType="outlined"
          isFullWidth
          type="submit"
          isLoading={isSubmitting || isLocalSubmitting}
        >
          <div className="flex items-center justify-center gap-2 py-1">
            <span className="flex-shrink-0">
              <img
                src={Assets?.images?.google}
                alt="Gmail"
                width={24}
                height={24}
                className="h-6 w-6 bg-transparent object-cover md:h-8 md:w-8"
              />
            </span>
            <span className="text-sm md:text-base">{t('openGmail')}</span>
          </div>
        </CustomButton>
        <CustomButton
          variant="surface"
          buttonType="outlined"
          isFullWidth
          type="submit"
          isLoading={isSubmitting || isLocalSubmitting}
        >
          <div className="flex items-center justify-center gap-2 py-1">
            <span className="flex-shrink-0">
              <img
                src={Assets?.images?.outlook}
                alt="Outlook"
                width={24}
                height={24}
                className="h-6 w-6 bg-transparent object-cover md:h-8 md:w-8"
              />
            </span>
            <span className="text-sm md:text-base">{t('openOutlook')}</span>
          </div>
        </CustomButton>
      </div>
    </Form>
  );

  return (
    <AuthLayout
      version={version}
      error={actionData?.error}
    >
      <div
        key={currentStep}
        // Pure CSS @keyframes animation for every step: the browser runs it
        // as soon as the stylesheet is parsed, independent of JS/hydration.
        // A JS-driven opacity flip (useEffect + requestAnimationFrame) was
        // tried here before, but it renders opacity-0 in the SSR markup and
        // only flips visible once React hydrates — on a slow connection
        // hydration can take many seconds, so the form stayed invisible
        // the whole time. CSS keyframes have no such dependency.
        className="flex h-full w-full animate-step-in flex-col"
      >
        <h4 className="h4 text-xl font-semibold leading-tight text-primarySemiDark md:text-2xl lg:text-3xl">
          {currentStep === 3
            ? t('resetPasswordHeading')
            : currentStep === 4
              ? t('checkYourEmail')
              : t('signInToAccount')}
        </h4>

        <div className="mb-2 md:mb-3" />

        <p className="text-sm leading-relaxed text-neutralDark md:text-base">
          {currentStep === 3
            ? t('passwordResetUnavailableMessage')
            : currentStep === 4
              ? t('resetEmailSentMessage', { email: resetEmail })
              : t('emailSuggestionLogin')}
        </p>

        <div className="mb-6 md:mb-8" />

        <div className="flex w-full flex-col">
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}
        </div>

        {/* PENDING: FIND-07 — re-enable once signup flow is confirmed ready
        <div className="mt-8 flex items-center justify-center gap-2">
          <p className="text-sm text-neutralMedium">{t('newToRaithu')}</p>
          <CustomButton
            variant="primary"
            buttonType="text"
            onClick={() => navigate(AppRoutes.SIGNUP.path)}
          >
            {t('createAnAccount')}
          </CustomButton>
        </div> */}
      </div>
    </AuthLayout>
  );
}
